/**
 * What every file the system hands a person is called — a download, an email
 * attachment, a signed copy, an export. One rule for every SaleFish app
 * (Console, Showroom, the Agent Portals) and PropPay LX, set 2026-10-27
 * (Andrew: "a standard naming convention for all documents and downloads from
 * the system that are telling and have the date appended"). It grows out of
 * PropPay LX's Document Library names (`PropPay LX - Purchaser Payment Guide -
 * v1.0 - 2026-10-08.pdf`), the one family of names that already read well in
 * a Downloads folder.
 *
 *     <Owner> - <Document> (<state>) - <Subject> - … - <YYYY-MM-DD>.<ext>
 *
 *   Bolton Place - Agreement of Purchase and Sale (executed) - Lot 12 - Jane Doe - 2026-10-27.pdf
 *   Bolton Place - Contacts - 2026-10-27.csv
 *   Mattamy Homes - Commission Statement - October 2026 - 2026-11-01.pdf
 *   SaleFish - Invoice 1042 - 2026-10-27.pdf
 *
 * - OWNER is whose document it is, as the reader would file it: the project
 *   ("Bolton Place"), or the company or app for anything wider than one
 *   project ("Mattamy Homes", "SaleFish", "PropPay LX").
 * - DOCUMENT is the title the app shows for it. A STATE in parentheses tells
 *   apart copies of one document: (draft), (signed), (executed), (void).
 * - SUBJECT parts say which one: the unit ("Lot 12", "Suite 1504"), the
 *   people, a number ("Amendment 2"), a version ("v1.2"), a period. None for
 *   a plain export.
 * - The DATE is always there and always last: the document's own day — the
 *   day it was executed, signed or issued, or for an export the day it was
 *   made — in the project's time zone (the reader's for anything wider).
 *   ISO, so a folder of them sorts by date within each name.
 *
 * Never in a name: a storage key, a uuid or a slice of one, a random suffix,
 * a timestamp, or anything private — an ID number, an email, a phone number.
 * A purchaser's name is fine; the document is theirs.
 *
 * This file is COPIED, unchanged, into each app (like the Showroom contract in
 * shared/showroomApi.ts). Change every copy or none; each app's own test
 * imports its copy and pins the same examples.
 */

/** Characters no desktop or phone file system accepts in a name. */
const UNSAFE = /[\\/:*?"<>|\u0000-\u001f\u007f]+/g;

/** The longest a name may be before its extension. Subjects give way first. */
export const FILE_NAME_MAX = 150;

export type FileNameParts = {
  /** The project, or the company or app for anything wider than a project. */
  owner: string;
  /** The document's own title, as the app shows it. */
  document: string;
  /** Which copy of it: "signed", "executed", "draft", "void". */
  state?: string | null;
  /** Which one: the unit, the people, a number, a version, a period. Blanks are dropped. */
  subject?: Array<string | null | undefined | false>;
  /** The document's own day. A Date is read in `timeZone`; a "YYYY-MM-DD" string is taken as written. */
  date: Date | string;
  /** IANA zone the date is read in — the project's. Defaults to Toronto, where every project is today. */
  timeZone?: string;
  /** "pdf", "csv", "xlsx", "zip", "png"… */
  ext: string;
};

/** One part of a name, made safe: no characters a file system refuses, no runs of spaces, no leading dots. */
export function cleanPart(s: string): string {
  return s
    .normalize("NFC")
    .replace(UNSAFE, "-")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[.\s-]+|[.\s-]+$/g, "");
}

/** The calendar day a moment falls on in `timeZone`, as YYYY-MM-DD. */
export function dayIn(date: Date | string, timeZone = "America/Toronto"): string {
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) throw new Error(`fileName: not a date: ${String(date)}`);
  // en-CA formats a date as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

/** The name a file is saved, attached and downloaded under. */
export function fileName(p: FileNameParts): string {
  const ext = cleanPart(p.ext.replace(/^\./, "")).toLowerCase() || "pdf";
  const owner = cleanPart(p.owner) || "SaleFish";
  const doc = cleanPart(p.document) || "Document";
  const state = p.state ? cleanPart(p.state) : "";
  const head = [owner, state ? `${doc} (${state})` : doc];
  const tail = dayIn(p.date, p.timeZone);
  const subject = (p.subject ?? []).filter((s): s is string => typeof s === "string").map(cleanPart).filter(Boolean);

  // Over length: drop subject parts from the end, then shorten the last one
  // kept. Owner, document and date always survive — they are what make the
  // name findable.
  const fixed = [...head, tail].join(" - ").length;
  while (subject.length && [...head, ...subject, tail].join(" - ").length > FILE_NAME_MAX) {
    const room = FILE_NAME_MAX - fixed - 3 * subject.length;
    const lastLen = subject[subject.length - 1].length;
    const othersLen = subject.slice(0, -1).reduce((n, s) => n + s.length, 0);
    if (room - othersLen >= 8 && room - othersLen < lastLen) {
      subject[subject.length - 1] = subject[subject.length - 1].slice(0, room - othersLen).trimEnd();
      break;
    }
    subject.pop();
  }
  return `${[...head, ...subject, tail].join(" - ")}.${ext}`;
}

/**
 * A Content-Disposition header that carries the name to every browser: a
 * plain-ASCII `filename` for old clients and the exact name as RFC 5987
 * `filename*`. `inline` opens it in the browser; the save name still applies.
 */
export function contentDisposition(name: string, disposition: "attachment" | "inline" = "attachment"): string {
  const ascii = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7e]/g, "_")
    .replace(/["\\]/g, "_");
  const encoded = encodeURIComponent(name).replace(/['()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  return `${disposition}; filename="${ascii}"; filename*=UTF-8''${encoded}`;
}
